import { motion } from "framer-motion";
import { Check, MapPin, CalendarRange, Images, Ruler } from "lucide-react";

const A = "https://customer-assets.emergentagent.com/job_drone-survey-pro-4/artifacts";

const HERO = `${A}/wc451ao8_DJI_1025%20%281%29.webp`;

const gallery = [
  { url: `${A}/ks8q0yor_DJI_0099%20-%20Copy.webp`, label: "Terminal & Runway Progress" },
  { url: `${A}/6d67q6kj_DJI_0168%20-%20Copy.webp`, label: "Terminal Roof Under Construction" },
  { url: `${A}/fz7tjc8n_DSC03114.webp`, label: "Check-in Concourse" },
  { url: `${A}/qkjfiocg_DSC03262.webp`, label: "Baggage Reclaim Hall" },
  { url: `${A}/ef4payda_DSC03279.webp`, label: "Terminal Facade & Apron" },
  { url: `${A}/vrhmuh6q_Screenshot%202026-07-08%20143107.webp`, label: "Completed Runway" },
  { url: `${A}/tpdzspgk_Screenshot%202026-07-08%20145223.webp`, label: "Runway Lighting at Dusk" },
  { url: `${A}/b5d3nv20_Screenshot%202026-07-08%20141534.webp`, label: "Southern Runway Construction" },
  { url: `${A}/cff8gkrz_Screenshot%202026-07-08%20141649.webp`, label: "Terminal & Pier Earthworks" },
  { url: `${A}/6qedsygv_DJI_0123.webp`, label: "Iconic Terminal Roof" },
  { url: `${A}/3cdk9ati_IMG_20260325_104722265.webp`, label: "Terminal Facade & Boarding Gates" },
  { url: `${A}/zt0u30py_Navi%20Mumbai%20Airport%20Air%20Traffic%20Control%20%28ATC%29%20Tower%20..%40navimumairport%20..%23navimumbai%20%23navimumbaia.jpg`, label: "Air Traffic Control Tower" },
  { url: `${A}/fabigqh1_IMG_20260331_174241822.webp`, label: "Completed Terminal & Runway" },
];

const highlights = [
  { icon: CalendarRange, value: "2022–2026", label: "4 Years Continuous Monitoring" },
  { icon: Images, value: "Thousands", label: "High-Resolution Progress Images" },
  { icon: Ruler, value: "Fixed-GPS", label: "Consistent Comparison Points" },
  { icon: MapPin, value: "On-Site", label: "Dedicated Team Deployment" },
];

const scope = [
  "Drone Construction Progress Photography",
  "Drone Progress Videography",
  "Scheduled Weekly & Monthly Documentation",
  "Fixed GPS Position Monitoring",
  "High-Resolution Aerial Imaging",
  "Professional Graphic-Edited Progress Videos",
  "Time-Lapse Construction Documentation",
  "Project Progress Reporting",
];

const contribution = [
  "4 Years of Continuous Project Monitoring",
  "Dedicated On-Site Team Deployment",
  "Thousands of High-Resolution Progress Images",
  "Professional Aerial Video Documentation",
  "Consistent Visual Records Throughout Construction",
];

export const FeaturedProject = () => {
  return (
    <section id="project" className="py-24 bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-sm bg-[#10193f] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">
            Flagship Project
          </span>
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-black text-[#10193f]">
            4 Years of Construction Progress Monitoring at Navi Mumbai International Airport
          </h2>
          <p className="mt-3 text-base sm:text-lg font-semibold text-[#8b8f99]">
            2022–2026 · Dedicated On-site Team · Weekly &amp; Monthly Monitoring
          </p>
          <p className="mt-5 text-[#52525b] leading-relaxed">
            SNESAR Drones proudly supported one of India&apos;s most significant
            infrastructure developments, providing construction progress
            photography and videography for the Navi Mumbai International Airport.
            From 2022 to 2026 our on-site team captured scheduled aerial and
            ground documentation — maintaining a consistent visual record from
            early construction through every major milestone.
          </p>
        </div>

        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative mt-10 rounded-sm overflow-hidden border border-gray-200"
          data-testid="project-hero-image"
        >
          <img
            src={HERO}
            alt="Navi Mumbai International Airport aerial view"
            className="w-full h-[320px] sm:h-[480px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#10193f]/80 via-[#10193f]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 sm:p-8">
            <div className="font-heading text-3xl sm:text-4xl font-black text-white">
              India&apos;s Aviation Future
            </div>
            <p className="mt-1 text-white/80 text-sm sm:text-base">
              Aerial progress documentation · Navi Mumbai, Maharashtra
            </p>
          </div>
        </motion.div>

        {/* Highlights */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-sm overflow-hidden">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div
                key={h.label}
                data-testid={`project-highlight-${i}`}
                className="bg-white p-6"
              >
                <Icon className="h-6 w-6 text-[#8b8f99]" strokeWidth={1.75} />
                <div className="mt-4 font-heading text-2xl font-black text-[#10193f]">
                  {h.value}
                </div>
                <div className="mt-1 text-sm text-[#52525b]">{h.label}</div>
              </div>
            );
          })}
        </div>

        {/* Scope + Contribution */}
        <div className="mt-14 grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="font-heading text-xl font-bold text-[#10193f]">
              Scope of Services
            </h3>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {scope.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-[#52525b]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8b8f99]" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-[#10193f]">
              Our Contribution
            </h3>
            <ul className="mt-5 space-y-3">
              {contribution.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-[#52525b]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8b8f99]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-14">
          <h3 className="font-heading text-xl font-bold text-[#10193f]">
            Project Gallery
          </h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {gallery.map((g, i) => (
              <motion.figure
                key={i}
                data-testid={`project-gallery-${i}`}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="group relative h-56 overflow-hidden rounded-sm border border-gray-200"
              >
                <img
                  src={g.url}
                  alt={g.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#10193f]/85 via-transparent to-transparent" />
                <figcaption className="absolute bottom-3 left-3 right-3 text-white font-heading font-bold text-sm">
                  {g.label}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
